import { useState } from 'react'
import { useLossReasons } from '@/hooks/useOpportunities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatCurrency } from '@/lib/utils'
import { Frown, Loader2 } from 'lucide-react'

type Period = 90 | 180 | 365 | 730

const PERIOD_LABELS: Record<number, string> = {
  90: '90d',
  180: '6m',
  365: '12m',
  730: '24m',
}

const BAR_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-cyan-500',
]

export function LossReasonsSection() {
  const [period, setPeriod] = useState<Period>(180)
  const { data, isLoading, error } = useLossReasons(period)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Frown className="h-5 w-5" />
            Análise de perdas
          </CardTitle>
          <div className="flex items-center gap-1 bg-muted rounded-md p-1">
            {([90, 180, 365, 730] as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  period === p ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Carregando…
          </div>
        )}
        {error && <div className="text-sm text-destructive py-4">Erro ao carregar análise de perdas</div>}
        {data && !isLoading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 pb-3 border-b">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Oportunidades perdidas</div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{data.total_lost_count}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Valor total perdido</div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{formatCurrency(data.total_lost_value)}</div>
              </div>
            </div>

            {data.reasons.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-8 px-4">
                <p className="font-medium text-foreground mb-1">Nenhuma oportunidade perdida no período</p>
                <p className="text-xs">Para alimentar este relatório, marque oportunidades como "Perdida" no Kanban informando o motivo.</p>
              </div>
            )}

            {data.reasons.length > 0 && (
              <div className="space-y-3">
                {data.reasons.map((r, i) => (
                  <div key={r.reason} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium text-sm truncate">{r.reason}</div>
                      <div className="text-xs text-muted-foreground flex-shrink-0">
                        {r.count} {r.count === 1 ? 'opp' : 'opps'} · ticket médio {formatCurrency(r.avg_deal_size)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full ${BAR_COLORS[i % BAR_COLORS.length]} transition-all`}
                          style={{ width: `${r.pct_of_value}%` }}
                        />
                      </div>
                      <div className="text-sm font-semibold w-32 text-right">
                        {formatCurrency(r.total_value)}
                        <span className="text-xs text-muted-foreground ml-1">({r.pct_of_value}%)</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
